import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { movies } from "@/mocks/movies";
import { newsList } from "@/mocks/news";
import MovieCard from "@/components/base/MovieCard";
import NewsListItem from "@/components/base/NewsListItem";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

const HOT_KEYWORDS = ["科幻", "悬疑", "治愈", "纪录片", "影评", "票房"];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [input, setInput] = useState(query);
  const [tab, setTab] = useState<"movie" | "news">("movie");

  useEffect(() => {
    document.title = query ? `搜索「${query}」 - 泡泡影视` : "搜索 - 泡泡影视";
    setInput(query);
  }, [query]);

  const keyword = query.trim().toLowerCase();

  const movieResults = useMemo(() => {
    if (!keyword) return [];
    return movies.filter((m) =>
      [m.title, m.region, m.category, ...m.genres, ...m.tags]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
  }, [keyword]);

  const newsResults = useMemo(() => {
    if (!keyword) return [];
    return newsList.filter((n) =>
      [n.title, n.summary, n.category, n.author, ...n.tags]
        .join(" ")
        .toLowerCase()
        .includes(keyword)
    );
  }, [keyword]);

  const submitSearch = (value: string) => {
    const trimmed = value.trim();
    setSearchParams(trimmed ? { q: trimmed } : {});
  };

  const results = tab === "movie" ? movieResults : newsResults;

  return (
    <div className="w-full px-4 md:px-8 pt-6">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-5 rounded-xl bg-primary-950 px-6 py-8 md:px-10 md:py-10">
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-background-50">
          搜索影视与资讯
        </h1>
        <form
          className="mt-5 flex flex-col sm:flex-row gap-3 max-w-2xl"
          onSubmit={(event) => {
            event.preventDefault();
            submitSearch(input);
          }}
        >
          <div className="flex-1 flex items-center gap-2 h-12 px-4 rounded-md bg-background-50">
            <i className="ri-search-line text-lg text-foreground-400" />
            <input
              type="search"
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入影片名、类型或关键词，例如：科幻、治愈"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
            />
          </div>
          <button
            type="submit"
            className="h-12 px-6 rounded-md bg-accent-500 text-primary-950 text-sm font-semibold hover:bg-accent-400 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm">
          <span className="text-background-400">热门搜索：</span>
          {HOT_KEYWORDS.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => {
                setInput(word);
                submitSearch(word);
              }}
              className="rounded-full bg-background-50/10 border border-background-50/25 px-3 py-1 text-background-200 hover:bg-background-50/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {query ? (
        <>
          <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
            <p className="text-sm text-foreground-600">
              搜索「<span className="font-semibold text-foreground-950">{query}</span>」，共找到{" "}
              <span className="font-semibold text-foreground-950">{results.length}</span> 条结果
            </p>
            <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setTab("movie")}
                className={`flex items-center gap-1 px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tab === "movie" ? "bg-background-50 text-primary-700 font-medium" : "text-foreground-600 hover:text-primary-700"
                }`}
              >
                <i className="ri-film-line" />
                影视资源 ({movieResults.length})
              </button>
              <button
                type="button"
                onClick={() => setTab("news")}
                className={`flex items-center gap-1 px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  tab === "news" ? "bg-background-50 text-primary-700 font-medium" : "text-foreground-600 hover:text-primary-700"
                }`}
              >
                <i className="ri-newspaper-line" />
                资讯 ({newsResults.length})
              </button>
            </div>
          </div>

          <div className="mt-6">
            {results.length === 0 ? (
              <EmptyState
                title="没有找到相关内容"
                description="换个关键词试试吧，比如影片类型、地区或资讯主题。你也可以浏览下面的热门内容。"
                actionLabel="查看全部影视分类"
                onAction={() => window.REACT_APP_NAVIGATE("/category")}
              />
            ) : tab === "movie" ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {movieResults.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-4 max-w-4xl">
                {newsResults.map((article) => (
                  <NewsListItem key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <div className="mt-8">
          <EmptyState
            title="输入关键词开始搜索"
            description="你可以搜索影片名、影视类型、地区、标签或资讯主题，我们会在影视资源与资讯中为你匹配结果。"
          />
        </div>
      )}
    </div>
  );
}